import { createStyles, Grid, WithStyles, withStyles } from '@material-ui/core';
import { FormikActions } from 'formik';
import { inject } from 'mobx-react';
import * as React from 'react';
import Button from 'src/common/Button/Button';
import ESDialog from 'src/common/ESDialog';
import FullWidthField from 'src/common/FullWidthField';
import { DefaultAttachmentFile, IBestPractice } from 'src/Definitions';
import { AttachmentFileDto } from 'src/generated_client';
import { IStores } from 'src/Stores';

type ISetFieldValue = (field: string, value: any) => void;

interface IBestPracticeDialogProps {
    bestPractice?: IBestPractice;
    close?: () => void;
    downloadFile?: (url: string) => void;
    isLoading?: boolean;
    isOpen?: boolean;
    saveBestPractice?: (bestPractice: IBestPractice) => Promise<void>;
    setHasFileChanged?: (hasFileChanged: boolean) => void;
}

const styles = () =>
    createStyles({
        fileInput: { display: 'none' },
    });

interface IBestPracticeFormValues {
    createdOn: Date;
    fileUrl: string;
    fileContent: string;
    fileName: string;
    fileMime: string;
    id: string;
    isActive: boolean;
    isNew: boolean;
    modifiedOn: Date;
    name: string;
    txtBestPracticeName: string;
    txtFileName: string;
}

class BestPracticeDialog extends React.Component<
    IBestPracticeDialogProps & WithStyles<typeof styles>
> {
    public state = {
        bestPracticeName: '',
        fileHasChanged: false,
        fileName: '',
        isFileLoading: false,
    };
    public upload: HTMLInputElement | null;

    public save = (
        values: IBestPracticeFormValues,
        formikBag: FormikActions<IBestPracticeFormValues>
    ) => {
        let newAttachmentFile;
        if (this.props.bestPractice!.isNew) {
            newAttachmentFile = new AttachmentFileDto({
                ...DefaultAttachmentFile(),
                content: values.fileContent,
                mimeType: values.fileMime,
                name: values.fileName,
                url: values.fileUrl,
            });
        } else if (this.state.fileHasChanged) {
            newAttachmentFile = this.props.bestPractice!.attachmentFile as AttachmentFileDto;
            newAttachmentFile.mimeType = values.fileMime;
            newAttachmentFile.content = values.fileContent;
            newAttachmentFile.name = values.name;
            newAttachmentFile.url = values.fileUrl;
            newAttachmentFile.modifiedOn = values.modifiedOn;
        } else {
            newAttachmentFile = this.props.bestPractice!.attachmentFile as AttachmentFileDto;
        }
        this.props.setHasFileChanged!(this.state.fileHasChanged);

        const newBestPractice = {
            attachmentFile: newAttachmentFile,
            attachmentFileId: newAttachmentFile.id,
            createdOn: values.createdOn,
            id: values.id,
            isActive: values.isActive,
            isNew: values.isNew,
            modifiedOn: values.modifiedOn,
            name: values.txtBestPracticeName === '' ? values.name : values.txtBestPracticeName,
        } as IBestPractice;
        this.props.saveBestPractice!(newBestPractice).finally(() => formikBag.setSubmitting(false));
    };

    public download = (values: IBestPractice) => () => {
        const bestPractice = { ...values };
        this.props.downloadFile!(bestPractice.attachmentFile!.url!);
    };

    public handleOnButtonClick = (target: BestPracticeDialog) => {
        return () => target.upload && target.upload.click();
    };

    public handleFileChange = (setFieldValue: ISetFieldValue) => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files && event.target.files[0]) {
                const file = event.target.files[0];
                const reader = new FileReader();
                reader.onloadstart = () => {
                    this.setState({
                        isFileLoading: true,
                    });
                };
                reader.onload = () => {
                    if (reader) {
                        const fileContent = (reader.result! as string).split(',')[1];
                        const fileMimeType = file.type.split('/')[1];
                        const fileName = file.name;
                        const fileUrl = reader.result;
                        this.setState({
                            fileHasChanged: true,
                            isFileLoading: false,
                        });
                        setFieldValue('fileContent', fileContent);
                        setFieldValue('name', fileName);
                        setFieldValue('fileName', fileName);
                        setFieldValue('fileMime', fileMimeType);
                        setFieldValue('fileUrl', fileUrl);
                        setFieldValue('txtBestPracticeName', fileName);
                        setFieldValue('txtFileName', fileName);
                    }
                };
                reader.readAsDataURL(file);
            }
        };
    };

    public handleNameChange = () => {
        return (event: React.ChangeEvent<HTMLInputElement>) => {
            this.setState({
                bestPracticeName: event.target.value,
            });
        };
    };

    public render() {
        const { classes, close, isLoading, isOpen, bestPractice } = this.props;
        const initialValues = {
            ...bestPractice,
            txtBestPracticeName: bestPractice!.name,
            txtFileName: !bestPractice!.isNew ? bestPractice!.attachmentFile!.name : '',
        } as IBestPracticeFormValues;
        this.state.bestPracticeName = this.props.bestPractice!.name;
        this.state.fileName = !bestPractice!.isNew
            ? this.props.bestPractice!.attachmentFile!.name || ''
            : '';
        const downloadButton = (
            <Button variant="contained" onClick={this.download(bestPractice!)}>
                Download
            </Button>
        );
        return (
            <ESDialog
                close={close!}
                initialValues={initialValues}
                isLoading={isLoading}
                open={isOpen!}
                save={this.save}
                title={bestPractice!.isNew ? 'Create Best Practice' : 'Edit Best Practice'}
                additionalButtons={bestPractice!.isNew ? [] : [downloadButton]}
            >
                {({ isSubmitting, setFieldValue }) => (
                    <Grid container spacing={40} alignItems="flex-end">
                        <Grid item>
                            <Button
                                component="label"
                                disabled={isSubmitting || isLoading}
                                isLoading={this.state.isFileLoading}
                                onClick={this.handleOnButtonClick(this)}
                                variant="contained"
                            >
                                Choose File
                            </Button>
                            <input
                                type="file"
                                className={classes.fileInput}
                                onChange={this.handleFileChange(setFieldValue)}
                                ref={(ref) => (this.upload = ref)}
                                style={{ display: 'none' }}
                            />
                        </Grid>
                        <Grid item>
                            <FullWidthField
                                name="txtFileName"
                                label="File Name"
                                InputLabelProps={{ shrink: true }}
                                disabled
                            />
                        </Grid>
                        <Grid item>
                            <FullWidthField
                                name="txtBestPracticeName"
                                label={'Best Practice name'}
                                InputLabelProps={{ shrink: true }}
                                onChange={this.handleNameChange}
                                disabled={isSubmitting || isLoading}
                            />
                        </Grid>
                    </Grid>
                )}
            </ESDialog>
        );
    }
}

const InjectedBestPracticeDialog = inject<
    IStores,
    IBestPracticeDialogProps,
    Partial<IBestPracticeDialogProps>,
    any
>((stores: IStores) => ({
    bestPractice: stores.bestPractices.selectedBestPractice,
    close: stores.bestPractices.closeDialog,
    downloadFile: stores.bestPractices.downloadBestPracticeFile,
    isLoading: stores.bestPractices.isLoading,
    isOpen: stores.bestPractices.isModalOpen,
    saveBestPractice: stores.bestPractices.saveBestPractice,
    setHasFileChanged: stores.bestPractices.setHasFileChanged,
}))(withStyles(styles)(BestPracticeDialog));

export default InjectedBestPracticeDialog;



// WEBPACK FOOTER //
// ./src/bestPractices/BestPracticeDialog.tsx